import type { Outcome } from 'pages/timecards/form/DescriptionImprove';
import { mismatchedDescriptionLanguage } from 'pages/timecards/form/descriptionLanguage';
import posthog from 'posthog-js';
import { TimerLog } from 'types/timecards';
import { isPostHogEnabled } from './posthog';

/**
 * Timer, timecard and description events. Nothing here sends a description, a duration, a name or an id: a timecard
 * goes out as the kind of save that made it, a finish as where its time came from, and a description as the language
 * it reads as.
 */

/**
 * `form` is the timecard form, the one the timecard center's Finish opens included; `tracker` is a draft saved from
 * the timecard center or a case's Start button.
 */
export type TimecardCreatedVia = 'form' | 'tracker';

/** `multi_author` is a finish with several authors only; an invoice save is `invoice` whatever its author count. */
export type TimecardCreatedKind = 'draft' | 'finished' | 'multi_author' | 'invoice';

/** `record` is the Timecard card of a timecard's record page; `invoice` a timecard row of an invoice. */
export type TimecardUpdatedVia = 'form' | 'record' | 'invoice';

/** Where a description is written, with its language warning and its AI suggestions. */
export type DescriptionVia = 'form' | 'record' | 'tracker';

/** `translate` is the suggestion asked for while the language warning shows. */
export type DescriptionSuggestionMode = 'translate' | 'improve';

function capture(event: string, properties: Record<string, unknown>): void {
    if (!isPostHogEnabled) {
        return;
    }
    posthog.capture(event, properties);
}

/** The text a save stored and its case's timecard language, the two it is checked by. */
export interface SavedDescription {
    description: string | null | undefined;
    timecardLanguageCode: string | null | undefined;
}

/**
 * Whether the saved text reads as another language than its case's, and which: nothing for a case without a timecard
 * language, nor when the detector fails to load.
 */
async function languageProperties({ description, timecardLanguageCode }: SavedDescription) {
    if (!timecardLanguageCode) {
        return {};
    }
    try {
        const language = await mismatchedDescriptionLanguage(description, timecardLanguageCode);
        return {
            description_language_mismatch: language !== null,
            timecard_language: timecardLanguageCode,
            ...(language === null ? {} : { description_language: language }),
        };
    } catch {
        return {};
    }
}

/** A save, sent once its text is checked. Called after the save went through, and never awaited. */
async function captureSave(event: string, properties: Record<string, unknown>, saved: SavedDescription) {
    if (isPostHogEnabled) {
        capture(event, { ...properties, ...(await languageProperties(saved)) });
    }
}

export interface TimecardCreatedEvent extends SavedDescription {
    via: TimecardCreatedVia;
    kind: TimecardCreatedKind;
    /** How many timecards the one save created, given where a save can create more than one. */
    timecardCount?: number;
}

export function captureTimecardCreated({ via, kind, timecardCount, ...saved }: TimecardCreatedEvent): Promise<void> {
    return captureSave(
        'timecard_created',
        { via, kind, ...(timecardCount === undefined ? {} : { timecard_count: timecardCount }) },
        saved
    );
}

/** An existing timecard was finished: `timer` when it holds time the stopwatch logged, `draft` when it holds none. */
export function captureTimecardFinished({
    timerLogs,
    ...saved
}: SavedDescription & { timerLogs: TimerLog[] | undefined }): Promise<void> {
    return captureSave('timecard_finished', { from: timerLogs?.length ? 'timer' : 'draft' }, saved);
}

export interface TimecardUpdatedEvent extends SavedDescription {
    via: TimecardUpdatedVia;
    /** Whether the save changed the stored description. */
    descriptionChanged: boolean;
}

/**
 * An existing timecard was saved from its form, its record page's card, or an invoice row's description or edit
 * dialog, whatever else the save changed. The stopwatch, the record page's status buttons, an invoice row's hours and
 * billing cells and the invoice bulk edits send none.
 */
export function captureTimecardUpdated({ via, descriptionChanged, ...saved }: TimecardUpdatedEvent): Promise<void> {
    return captureSave('timecard_updated', { via, description_changed: descriptionChanged }, saved);
}

/** The language warning appeared, on a check that found the text in another language where the one before had not. */
export function captureLanguageWarningShown({
    via,
    timecardLanguageCode,
    descriptionLanguage,
}: {
    via: DescriptionVia;
    timecardLanguageCode: string;
    /** The eld code the check read, such as `en`. */
    descriptionLanguage: string;
}): void {
    capture('language_warning_shown', {
        via,
        timecard_language: timecardLanguageCode,
        description_language: descriptionLanguage,
    });
}

/** The AI answered a suggestion request, whether or not the answer is still shown. */
export function captureDescriptionSuggestionReceived({
    mode,
    outcome,
    via,
    timecardLanguageCode,
}: {
    mode: DescriptionSuggestionMode;
    outcome: Outcome;
    via: DescriptionVia;
    timecardLanguageCode: string | null | undefined;
}): void {
    capture('description_suggestion_received', {
        mode,
        outcome: outcome.toLowerCase(),
        via,
        ...(timecardLanguageCode ? { timecard_language: timecardLanguageCode } : {}),
    });
}

/** `usage_limit` is the tenant's monthly AI limit reached. */
export function captureDescriptionSuggestionFailed({
    mode,
    reason,
    via,
}: {
    mode: DescriptionSuggestionMode;
    reason: 'usage_limit' | 'error';
    via: DescriptionVia;
}): void {
    capture('description_suggestion_failed', { mode, reason, via });
}

/** "Use this text" put the suggestion in the field. */
export function captureDescriptionSuggestionUsed({
    mode,
    via,
}: {
    mode: DescriptionSuggestionMode;
    via: DescriptionVia;
}): void {
    capture('description_suggestion_used', { mode, via });
}

/** Undo put the user's own text back; `mode` is the suggestion used last's. */
export function captureDescriptionSuggestionUndone({
    mode,
    via,
}: {
    mode: DescriptionSuggestionMode;
    via: DescriptionVia;
}): void {
    capture('description_suggestion_undone', { mode, via });
}

export function captureTimerStarted(): void {
    capture('timer_started', {});
}

/** A pause from the play/pause button. The pause a Finish press runs first is a stop, not a pause. */
export function captureTimerPaused(): void {
    capture('timer_paused', {});
}

/** A Finish press ended a running stopwatch. */
export function captureTimerStopped(): void {
    capture('timer_stopped', {});
}
